import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import * as authApi from '../api/auth.js';
import { extractApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { GoogleSignInButton } from '../components/GoogleSignInButton.js';
import { ANALYTICS_EVENTS, trackAuthEvent } from '../lib/analytics/index.js';
import argoLogo from '../assets/argo-logo.svg';

type LocationState = { from?: string; resetMessage?: string; email?: string } | null;

export function Login(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['auth', 'common']);
  const state = location.state as LocationState;
  const redirectTo = state?.from ?? '/quotes';
  const resetMessage = state?.resetMessage ?? null;

  const [email, setEmail] = useState(state?.email ?? '');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fallbackAction, setFallbackAction] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sent'>('idle');

  return (
    <div className="auth">
      <img className="auth-logo" src={argoLogo} alt={t('login.logoAlt')} />
      <h1 className="auth-title">{t('login.title')}</h1>
      <p className="auth-subtitle">{t('login.subtitle')}</p>
      {resetMessage && <div className="banner banner--success">{resetMessage}</div>}
      <div className="auth-section">
        <GoogleSignInButton />
      </div>
      <div className="auth-divider">{t('login.emailSignInDivider')}</div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErrorMessage(null);
          setFallbackAction(null);
          setSubmitting(true);
          void (async () => {
            try {
              await auth.login(email, password);
              // Password sign-in only. The Google path completes in
              // LoginCallback, which emits its own with method='google'.
              trackAuthEvent(ANALYTICS_EVENTS.login_succeeded, email, { method: 'password' });
              navigate(redirectTo, { replace: true });
            } catch (err) {
              const envelope = extractApiError(err);
              setErrorMessage(envelope?.message ?? t('login.errors.signInFailed'));
              setFallbackAction(envelope?.fallback_action ?? null);
            } finally {
              setSubmitting(false);
            }
          })();
        }}
      >
        <div className="auth-fields">
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            placeholder={t('login.emailPlaceholder')}
            aria-label={t('login.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            placeholder={t('login.passwordPlaceholder')}
            aria-label={t('login.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="auth-aux">
          <Link to="/password-reset">{t('login.forgotPassword')}</Link>
        </div>
        {errorMessage && (
          <div className="banner banner--error">
            <strong>{errorMessage}</strong>
            {fallbackAction === 'resend_verification' && (
              <div style={{ marginTop: 8 }}>
                {resendStatus === 'idle' ? (
                  <button
                    type="button"
                    onClick={() => {
                      void (async () => {
                        try {
                          await authApi.resendVerification(email);
                        } finally {
                          setResendStatus('sent');
                        }
                      })();
                    }}
                  >
                    {t('login.resendVerification')}
                  </button>
                ) : (
                  <span>{t('login.resendConfirmation')}</span>
                )}
              </div>
            )}
          </div>
        )}
        <button className="primary auth-submit" type="submit" disabled={submitting}>
          {submitting ? t('login.submitting') : t('common:nav.signIn')}
        </button>
        <p className="auth-alt">
          <Trans
            i18nKey="login.registerPrompt"
            t={t}
            components={[<Link key="register" to="/register" />]}
          />
        </p>
      </form>
    </div>
  );
}
